import React, { useEffect, useState } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useAllCareerFocuses } from '../../hooks/useAllCareerFocuses';
import { useContentLinks } from '../../hooks/useContentLinks';
import { BriefcaseIcon, ArchiveBoxIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { formatDateOnly } from '../../utils/dateUtils';

interface FocusHistoryDisplayProps {
  itemType: 'career_card' | 'note';
  itemId: string;
  className?: string;
}

interface LinkedFocus {
  focusId: string;
  goalIndices: number[];
  company?: string;
  role?: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'inactive' | 'upcoming';
  goals: string[];
}

export const FocusHistoryDisplay: React.FC<FocusHistoryDisplayProps> = ({
  itemType,
  itemId,
  className = ''
}) => {
  const { darkMode } = useDarkMode();
  const { focuses, loading: focusesLoading } = useAllCareerFocuses();
  const { getFilteredLinks } = useContentLinks();
  const [linkedFocuses, setLinkedFocuses] = useState<LinkedFocus[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const bgColor = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-200' : 'text-gray-800';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';

  useEffect(() => {
    const loadLinkedFocuses = async () => {
      if (!itemId || focusesLoading) return;

      try {
        // Get all focus links for this item
        const links = await getFilteredLinks({
          sourceType: itemType,
          sourceId: itemId,
          targetType: 'career_focus'
        });

        // Map links to focus details
        const linkedFocusData: LinkedFocus[] = [];
        
        for (const focus of focuses) {
          const focusLinks = links.filter(link => link.target_id === focus.id);
          if (focusLinks.length > 0) {
            const goalIndices = focusLinks
              .map(link => link.metadata?.goal_index)
              .filter((index): index is number => typeof index === 'number');
            
            linkedFocusData.push({
              focusId: focus.id,
              goalIndices: Array.from(new Set(goalIndices)),
              company: focus.company || undefined,
              role: focus.role || undefined,
              startDate: focus.start_date,
              endDate: focus.end_date,
              status: focus.status,
              goals: focus.goals
            });
          }
        }

        setLinkedFocuses(linkedFocusData);
      } catch (error) {
        console.error('Failed to load linked focuses:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLinkedFocuses();
  }, [itemId, itemType, focuses, focusesLoading]); // Remove getFilteredLinks as it's a stable function

  if (isLoading || focusesLoading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
      </div>
    );
  }

  if (linkedFocuses.length === 0) {
    return null;
  }

  // Separate active and inactive focuses
  const activeFocuses = linkedFocuses.filter(f => f.status === 'active');
  const inactiveFocuses = linkedFocuses.filter(f => f.status === 'inactive');

  // Only show inactive focuses since active ones are shown in the main linked content section
  if (inactiveFocuses.length === 0) {
    return null;
  }

  return (
    <div className={`${className} space-y-4`}>
      {/* Past Internships Only */}
      {inactiveFocuses.length > 0 && (
        <div>
          <h4 className={`text-xs font-medium ${mutedTextColor} mb-2 flex items-center gap-2`}>
            <ArchiveBoxIcon className="w-4 h-4" />
            Past Internships
          </h4>
          <div className="space-y-2">
            {inactiveFocuses.map(focus => (
              <div
                key={focus.focusId}
                className={`${bgColor} ${borderColor} border rounded-lg p-3 opacity-75`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className={`font-medium ${textColor}`}>
                      {focus.company} - {focus.role}
                    </div>
                    <div className={`text-xs ${mutedTextColor} mt-1`}>
                      {formatDateOnly(focus.startDate)} - {formatDateOnly(focus.endDate)}
                    </div>
                    {focus.goalIndices.length > 0 && (
                      <div className={`text-sm ${mutedTextColor} mt-2`}>
                        <span className="font-medium">Linked to {focus.goalIndices.length} goal{focus.goalIndices.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                    Completed
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};